import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { AbstractControl, TouchedChangeEvent } from '@angular/forms';
import { Message } from 'primeng/message';
import { ERROR_MESSAGE } from '../../models/error-messages';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-form-control-error',
  imports: [Message],
  templateUrl: './form-control-error.component.html',
  styleUrl: './form-control-error.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormControlErrorComponent implements OnInit {
  public formControl = input.required<AbstractControl>();
  readonly #destroyRef = inject(DestroyRef);
  protected readonly errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    this.#initListeners();
    this.#checkErrorsAndUpdateErrorMessage();
  }

  #initListeners(): void {
    this.formControl()
      .statusChanges.pipe(
        tap(() => {
          this.#checkErrorsAndUpdateErrorMessage();
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
    this.formControl()
      .events.pipe(
        tap((event) => {
          if (event instanceof TouchedChangeEvent) {
            this.#checkErrorsAndUpdateErrorMessage();
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #checkErrorsAndUpdateErrorMessage(): void {
    const errors = this.formControl().errors;

    if (errors && this.#shouldShowError()) {
      const firstErrorKey = Object.keys(errors)[0];
      const errorMessageFactory =
        ERROR_MESSAGE[firstErrorKey] ?? ERROR_MESSAGE['default'];

      this.errorMessage.set(errorMessageFactory(errors[firstErrorKey]));
    } else {
      this.errorMessage.set(null);
    }
  }

  #shouldShowError(): boolean {
    return (
      this.formControl().invalid &&
      (this.formControl().dirty || this.formControl().touched)
    );
  }
}
